/* eslint-disable react/display-name */
import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Cropper } from 'react-cropper';
import isEmail from 'validator/lib/isEmail';
import isMobilePhone from 'validator/lib/isMobilePhone';
import {
  Button,
  DataGrid,
  Drawer,
  PopupMenu,
  TextInputWithLabel,
  Loader,
  FileUploader
} from 'quantux-ui';
import { addAppAlert } from '../../actions';
import { blue, gray, salmon } from '../../theme';
import noPinIcon from '../../imgs/no-pin.svg';
import leftPinIcon from '../../imgs/left-pin.svg';
import rightPinIcon from '../../imgs/right-pin.svg';
import {
  BASE_URL,
  titleCase,
  generateDisplayName,
  DRAWER_BREAK_POINT,
  USA_STATES
} from '../../utils';
import PracticeUserCard from '../my-practices/practice-user-card';

let controller = new AbortController();
const DOCS_PER_PAGE = 10;
const defaultCellStyle = {
  font: '12px Lato',
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  // borderBottom: '0.5px solid lime',
  // borderRight: '0.5px solid lime',
  lineHeight: '14px'
};
const icons = {
  filter: { isSvg: false, path: '', class: 'fa fa-filter' },
  stringSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  stringSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  numberSortAscending: { isSvg: false, class: 'fa fa-numeric-asc' },
  numberSortDescending: { isSvg: false, class: 'fa fa-numeric-desc' },
  dateSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  dateSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  leftPin: { isSvg: true, path: leftPinIcon },
  rightPin: { isSvg: true, path: rightPinIcon },
  noPin: { isSvg: true, path: noPinIcon },
  menuIcon: { isSvg: false, class: 'fa fa-ellipsis-v' }
};
const headerDefs = [
  // Facility Name
  {
    visible: true,
    dataType: 'string',
    pin: 'left',
    sortable: false,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Facility Name',
    field: 'facilityName',
    key: 'facilityName',
    isCustomFilter: false,
    showFilterHint: false,
    filter: true,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'contains', 'endsWith', 'advanced'],
    colStyles: { width: 281, minWidth: 281, maxWidth: 281, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: (text) => {
      const data = text.split('|');
      return (
        <div className='cursor__pointer fx fx__v-center'>
          <img
            src={data[1]}
            alt='Facility Avatar'
            className='cursor__pointer'
            style={{ maxHeight: '42px' }}
          />
          <div className='ml-2 cursor__pointer'>
            {data[0]}
          </div>
        </div>
      );
      // const data = text.split('/');
      // return (
      //   <React.Fragment>
      //     <div style={{ color: '#0080FF', cursor: 'pointer', fontSize: '12px' }}>
      //       {data[0]}
      //     </div>
      //     <div style={{ color: '#0080FF', cursor: 'pointer', fontSize: '12px' }}>
      //       {`NPI: ${data[1]}`}
      //     </div>
      //   </React.Fragment>
      // );
    }
  },
  // Mailing address ()
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    sortable: false,
    sortOrder: 0,
    enableCellClick: false,
    text: 'Mailing Address',
    field: 'mailingAddress',
    key: 'mailingAddress',
    showFilterHint: true,
    isCustomFilter: true,
    filterHint: 'Enter city, state code, or ZIP',
    filter: true,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    colStyles: { width: 240, minWidth: 240, maxWidth: 240, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: text => {
      // Format address and zip
      const addTmp = text.split(' ');
      let zip = addTmp.pop();
      zip = zip.length <= 5 ? zip : zip.substr(0, 5) + '-' + zip.substr(5, 9);
      // Generate Google map url
      // const addressUrl = "https://www.google.com/maps/search/?api=1&query=" + encodeURI(text);
      const addressUrl = 'https://www.google.com/maps/search/?api=1&query=' + encodeURI(`${addTmp.join(' ')} ${zip.substr(0, 5)}`);
      return (
        <div className="fx fx__v-center">
          <a
            target="_blank"
            href={addressUrl}
            rel="noreferrer"
            className="mr-2"
            style={{ fontSize: '16px' }}
          >
            <i className="fa fa-map-marker" />
          </a>
          <a
            target="_blank"
            href={addressUrl}
            rel="noreferrer"
            className="mr-2"
          >
            <div>
              {addTmp.join(' ') + ' ' + zip}
            </div>
          </a>
        </div>
      );
    }
  },
  // Phone
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    sortable: false,
    sortOrder: 0,
    enableCellClick: false,
    text: 'Phone',
    field: 'phone',
    key: 'phone',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'none',
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    filterMenuType: 'advanced',
    basicFilterType: 'contains',
    advancedFilterType: 'containsAny',
    colStyles: { width: 123, minWidth: 123, maxWidth: 123, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: text => (
      <div>
        <a
          href={`tel:+1${text}`}
        >
          {`${text.substr(0, 3)}-${text.substr(3, 3)}-${text.substr(6, 4)}`}
        </a>
      </div>
    )
  },
  // Users
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    sortable: false,
    sortOrder: 0,
    enableCellClick: false,
    text: 'Facility Users',
    field: 'facilityUsers',
    key: 'facilityUsers',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'none',
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    filterMenuType: 'advanced',
    basicFilterType: 'contains',
    advancedFilterType: 'containsAny',
    colStyles: { width: 240, minWidth: 240, maxWidth: 240, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: (text) => {
      console.log('Names', text);
      const names = text.split('_');
      const comps = [];
      names.forEach((el, idx) => {
        comps.push(
          <div key={`${idx}_${Date.now()}`}>
            {el}
          </div>
        );
      });
      return (
        <React.Fragment>
          {comps}
        </React.Fragment>
      );
    }
  }
];

const FacilityDataGrid = (props) => {
  const isFirstRender = useRef(true);
  const [showLoader, setShowLoader] = useState(false);
  const [gridData, setGridData] = useState([]);
  const [filters, setFilters] = useState({});
  const [sorting, setSorting] = useState({});
  const [docsCount, setDocsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  // For add user/provider drawers and copper
  const [isBannerCropperDrawerOpen, toggleBannerCropperDrawer] = useState(false);
  const [isAvatarCropperDrawerOpen, toggleAvatarCropperDrawer] = useState(false);
  const [isAddFacilityDrawerVisible, toggleAddFacilityDrawer] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [primaryTaxonomyDes, setPrimaryTaxonomyDes] = useState('');
  const [isDisplayNameValid, checkDisplayNameValidity] = useState(false);
  const [isDisplayNamePristine, setDisplayNamePristineState] = useState(true);
  const [userName, setUserName] = useState('');
  const [isUserNameValid, checkUserNameValidity] = useState(false);
  const [isUserNamePristine, setUserNamePristineState] = useState(true);
  const [isUserNameDuplicate, setUserNameDuplicateStatus] = useState(true);
  const [streetAddress, setStreetAddress] = useState('');
  const [suite, setSuite] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [phone, setPhone] = useState('');
  const [isPhoneValid, checkPhoneValidity] = useState(false);
  const [isPhonePristine, setPhonePristineState] = useState(true);
  const [fax, setFax] = useState('');
  const [profileDescription, setProfileDescription] = useState('');
  const [bannerImageUrl, setBannerImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/defaultBanner.svg');
  const [avatarImageUrl, setAvatarImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/defaultAvatar.svg');
  const [showBannerCropper, setShowBannerCropper] = useState(false);
  const [isBannerUploading, setIsBannerUploading] = useState(false);
  const [showAvatarCropper, setShowAvatarCropper] = useState(false);
  const [isAvatarUploading, setIsAvatarUploading] = useState(false);
  const [email, setEmail] = useState('');
  const [isEmailValid, checkEmailValidity] = useState(false);
  const [isEmailPristine, setEmailPristineState] = useState(true);
  const [isEmailDuplicate, setEmailDuplicateStatus] = useState(true);
  const [npi, setNpi] = useState('');
  const [website, setWebsite] = useState('');
  const [canAddFacility, setCanAddFacility] = useState(false);
  const [facilityNpiSearchResults, setFacilityNpiSearchResults] = useState([]);
  const [showGridEllipsisMenu, toggleGridEllipsisMenu] = useState(false);
  const [cursorX, setCursorX] = useState(0);
  const [cursorY, setCursorY] = useState(0);
  const [popupMenuIndex, setPopupMenuIndex] = useState(-1);

  const [activeTab, setActiveTab] = useState(0);
  const [facilityName, setFacilityName] = useState('');
  const [facilityTaxonomy, setFacilityTaxonomy] = useState('');
  const [facilityUser, setFacilityUser] = useState('');
  const [facilityUserNpi, setFacilityUserNpi] = useState('');
  const [facilityUserId, setFacilityUserId] = useState('');
  const [facilityUserAvatarImageUrl, setFacilityUserAvatarImageUrl] = useState('');
  const [canAddFacilityUser, setCanAddFacilityUser] = useState(false);
  const [showFacilityUsersSearchMenu, toggleFacilityUsersSearchMenu] = useState(false);
  const [facilityUsersSearchResults, setFacilityUsersSearchResults] = useState([]);
  const [facilityUsers, setFacilityUsers] = useState([]);
  // Facility Search
  const [showSearchFacilityDrawer, setShowSearchFacilityDrawer] = useState(false);
  const [searchOrgName, setSearchOrgName] = useState('');
  const [searchTaxonomy, setSearchTaxonomy] = useState('');
  const [searchNpi, setSearchNpi] = useState('');
  const [showNpiErrMessage, setShowNpiErrMessage] = useState(false);
  const [canSearch, setCanSearch] = useState(false);
  const [npiErrMsg, setNpiErrMsg] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [searchZip, setSearchZip] = useState('');
  const [showStateMenu, setShowStateMenu] = useState(false);
  const [searchState, setSearchState] = useState('');
  const [searchingFacilities, setSearchingFacilities] = useState(false);
  const [facilitySearchResults, setFacilitySearchResults] = useState([]);
  const [showZeroResultMessage, setShowZeroResultMessage] = useState(false);

  const [gridWidth, setGridWidth] = useState(window.innerWidth <= 768 ? window.innerWidth - 16 : 897);
  const [gridHeight, setGridHeight] = useState(window.innerHeight - 134);
  const [isMobileLayout, setMobileLayout] = useState(false);
  // ---
  const [isFacilitySelected, setIsFacilitySelected] = useState(false);

  // Refs
  const lastNameSearchMenu = React.createRef();
  const drawerBannerRef = React.createRef();
  const drawerAvatarRef = React.createRef();
  const bannerCropperRef = React.createRef();
  const bannerFileInputRef = React.createRef();
  const avatarCropperRef = React.createRef();
  const avatarFileInputRef = React.createRef();
  const gridEllipsisMenu = React.createRef();
  const facilityUserRef = React.createRef();
  const gridParentRef = useRef();
  const stateRef = useRef()

  // Styles
  const styles = StyleSheet.create({
    gridParent: {
      overflow: 'hidden',
      padding: '0 8px'
    },
    gridContainer: {
      // maxWidth: '804px',
      width: `${gridWidth}px`,
      overflow: 'hidden'
    },
    gridToolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'right',
      height: '40px',
      border: '1px solid black',
      paddingLeft: '4px',
      background: gray[500]
    },
    gridToolbarIcon: {
      height: '50px',
      width: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      ':hover': {
        color: blue[500]
      }
    },
    addUserMenu: {
      width: '184px',
      position: 'relative',
      marginBottom: '8px'
    },
    usersSearchMenu: {
      width: '350px',
      background: gray[700],
      overflow: 'hidden'
    },
    userSearchMenuHeading: {
      padding: '8px 0',
      textAlign: 'center'
    },
    userSearchMenuSearchingMsg: {
      color: blue[500],
      fontSize: '1.25rem',
      textAlign: 'center',
      padding: '24px'
    },
    userSearchMenuItems: {
      maxHeight: '250px',
      overflowY: 'auto'
    },
    userSearchMenuResultsMsg: {
      display: 'flex',
      flexFlow: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '120px'
    },
    drawerContainer: {
      // background: gray[500],
      // background: gray[500],
      background: '#002448',
      color: gray[300],
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column'
    },
    drawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0'
    },
    drawerHeaderCloseIcon: {
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    drawerBanner: {
      position: 'relative',
      padding: '8px 0 0 2px',
      flexShrink: '0'
    },
    drawerBannerImg: {
      width: '100%',
      height: 'auto'
    },
    drawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '0',
      bottom: '8px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    drawerContentArea: {
      // height: 'calc(100vh - 186px)',
      overflowY: 'auto',
      color: gray[300],
      flexGrow: '1'
    },
    drawerUserCardAvatar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '80px',
      height: '80px',
      color: 'white',
      borderRadius: '4px',
      position: 'relative'
    },
    drawerUserCardAvatarImg: {
      width: 'auto',
      height: '80px',
      maxHeight: '80px'
    },
    drawerAvatarCamera: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      left: '-3px',
      top: '58px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    drawerUserCardText: {
      fontSize: '12px'
    },
    profileTextAreaContainer: {
      paddingRight: '8px'
    },
    profileTextArea: {
      width: '100%',
      background: gray[900],
      height: '100px',
      color: gray[100],
      border: 'none',
      resize: 'none',
      overflow: 'auto',
      fontFamily: 'inherit',
      ':focus': {
        outlineColor: 'transparent',
        outlineStyle: 'none'
      }
    },
    drawerFooter: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      background: gray[700],
      height: '56px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      flexShrink: '0'
    },
    imageUploaderContainer: {
      position: 'relative',
      maxHeight: '454px'
    },
    imageUploadLoader: {
      position: 'relative',
      height: '454px',
      top: '-400px',
      background: gray[800]
    },
    criticalSmallText: {
      fontSize: '12px',
      color: salmon[500],
      paddingLeft: '8px',
      fontWeight: 'bold',
      width: '140px'
    },
    userSearchItems: {
      cursor: 'pointer',
      ':hover': {
        background: gray[800]
      }
    },
    userSearchItemsIcon: {
      width: '40px',
      height: '40px',
      background: blue[500],
      color: 'white',
      fontSize: '28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '5px'
    },
    errMsg: {
      color: '#FF6060',
      fontSize: '12px'
    },
    gridEllipsisMenuParent: {
      position: 'fixed',
      height: '64px',
      width: '140px',
      background: 'black',
      zIndex: '100'
    },
    gridEllipsisMenuContainer: {
      minWidth: '140px'
    },
    gridEllipsisMenuItem: {
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      width: '138px',
      paddingLeft: '8px',
      background: '#103A60',
      ':hover': {
        background: '#0066CC'
      }
    },
    npiErrMessage: {
      color: salmon[500],
      fontSize: '14px',
    },
    npiErrMessageContainer: {
      textAlign: 'right',
      paddingRight: '8px',
      paddingTop: '4px'
    }
  });

  const history = useHistory();

  const handleWindowResize = () => {
    if (gridParentRef && gridParentRef.current) {
      const gridParentRefWidth = gridParentRef.current.getBoundingClientRect().width;
      console.log(`gridParentRef width = ${gridParentRefWidth}`);
      // const tmp = window.innerWidth <= 768 ? window.innerWidth - 16 : Math.ceil(0.75 * gridParentRefWidth);
      const tmp = window.innerWidth <= 768 ? window.innerWidth - 16 : Math.ceil(0.9 * gridParentRefWidth);
      console.log(`GridWidth = ${tmp}`);
      setGridWidth(Math.min(tmp, 897));
      setGridHeight(window.innerHeight - 134);
      setMobileLayout(window.innerWidth <= DRAWER_BREAK_POINT);
    }
  };
  // -- Effects ---------------------------------------------------------
  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    getData();
    handleWindowResize();
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  // Search facility users
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const ddF = setTimeout(() => {
      if (facilityUser.length > 0) {
        searchPracticeOwners();
      }
    }, 300);
    return () => clearTimeout(ddF);
  }, [facilityUser]);

  // NPPES and Medpors profiles search by npi
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const ddF = setTimeout(() => {
      if (npi.length > 0 && isAddFacilityDrawerVisible) {
        searchFacilitiesByNpi();
      }
    }, 300);
    return () => clearTimeout(ddF);
  }, [npi]);

  // Check for duplicate username
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (userName.length >= 2 && userName.length <= 20 && userName.indexOf(' ') < 0) {
      const url = new URL(`${BASE_URL}/facility/isFacilityUserNameExists`);
      const params = { userName };
      url.search = new URLSearchParams(params).toString();
      fetch(url)
        .then(res => res.text())
        .then((result) => {
          const data = JSON.parse(result);
          // console.log(data);
          setUserNameDuplicateStatus(data.isDuplicate);
        })
        .catch(error => console.log('error', error));
    }
  }, [userName]);

  // Check for duplicate email
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (isEmailValid) {
      const url = new URL(`${BASE_URL}/practice/isPracticeEmailExists`);
      const params = { email };
      url.search = new URLSearchParams(params).toString();
      fetch(url)
        .then(res => res.text())
        .then((result) => {
          const data = JSON.parse(result);
          setEmailDuplicateStatus(data.isDuplicate);
        })
        .catch(error => console.log('error', error));
    }
  }, [email]);

  // Can add facility flag check
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const value =
      displayName.length >= 2 && displayName.length <= 40 &&
      userName.length >= 2 && userName.length <= 20 && userName.indexOf(' ') < 0 &&
      !isUserNameDuplicate &&
      // streetAddress.length > 5 &&
      // city.length >= 2 &&
      // state.length === 2 &&
      // zip.length >= 5 &&
      isEmailValid &&
      !isEmailDuplicate
    // isPhoneValid;
    // facilityUsers.length > 0 && (facilityUsers.findIndex(el => el.accessType === 'Practice Owner') > -1);
    setCanAddFacility(value);
  }, [
    displayName,
    userName,
    isUserNameDuplicate,
    streetAddress,
    suite,
    city,
    state,
    zip,
    email,
    isEmailValid,
    isEmailDuplicate,
    phone,
    isPhoneValid,
    facilityUsers
  ]);

  useEffect(() => {
    if (searchNpi.length === 0) {
      setShowNpiErrMessage(false);
      setNpiErrMsg('');
    } else if (isNaN(parseInt(searchNpi))) {
      setNpiErrMsg('NPI should only have digits');
      setShowNpiErrMessage(true);
    } else {
      if (searchNpi.length < 10 || searchNpi.length > 10 || searchNpi.indexOf(' ') > -1) {
        setShowNpiErrMessage(true);
        setNpiErrMsg('Atleast 10 digits');
      } else {
        setShowNpiErrMessage(false);
        setNpiErrMsg('');
      }
    }
  }, [searchNpi]);

  useEffect(() => {
    if (searchNpi.length === 0 && searchOrgName.length === 0) {
      setCanSearch(false);
    } else {
      if (searchOrgName.length > 0 && searchNpi.length === 0) {
        setCanSearch(true);
      } else if (searchOrgName.length === 0 && searchNpi.length > 0) {
        setCanSearch(!showNpiErrMessage);
      } else {
        setCanSearch(!showNpiErrMessage);
      }
    }
  }, [showNpiErrMessage, searchOrgName, searchNpi]);

  // #region Network Requests
  const getData = async () => {
    setShowLoader(true);
    // Facilities data reqeust
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    reqHeaders.append('Content-Type', 'application/x-www-form-urlencoded');

    const formData = new FormData();
    formData.append('skip', '10');
    formData.append('limit', '80');

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    const dataPromise = fetch(`${BASE_URL}/facility/facilities`, requestOptions);
    const countPromise = fetch(`${BASE_URL}/facility/count`, requestOptions);

    const response = await Promise.all([dataPromise, countPromise])
      .catch(err => {
        console.log('Error getting facility grid data and count');
        console.log(err);
      });

    if (response) {
      const facilityDataResponse = await response[0].json();
      const facilityCountResponse = await response[1].json();

      if (facilityCountResponse && facilityDataResponse.success) {
        console.log(facilityCountResponse);
        setDocsCount(facilityCountResponse.facilityCount);
      }

      if (facilityDataResponse && facilityDataResponse.success) {
        const data = [];
        for (let i = 0; i < facilityDataResponse.facilities.length; i++) {
          const activeRecord = facilityDataResponse.facilities[i];
          const add1 = activeRecord.mailingAddress.streetAddress1;
          const suite = '';
          const city = activeRecord.mailingAddress.city;
          const state = activeRecord.mailingAddress.state;
          const zip = activeRecord.mailingAddress.zip;
          let facilityUsers = '';
          console.log(activeRecord.users);
          activeRecord.users.forEach((el, idx) => {
            facilityUsers += el.fullName;
            if (idx !== activeRecord.users.length - 1) {
              facilityUsers += '_';
            }
          });

          data.push({
            id: activeRecord._id,
            // facilityName: `${activeRecord.facilityName}/${activeRecord.npi}/${activeRecord._id}`,
            facilityName: `${activeRecord.displayName}|${activeRecord.avatarImageUrl}`,
            mailingAddress: `${add1} ${suite} ${city}, ${state} ${zip}`,
            phone: activeRecord.phone.replace(/-/g, ''),
            npi: `${activeRecord.npi}`,
            facilityUsers
          });
        }
        setGridData(data);
        setShowLoader(false);
      }
    }
  };

  const getUniqueFacilityUsername = (organizationName) => {
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    reqHeaders.append('Content-Type', 'application/x-www-form-urlencoded');

    const url = new URL(`${BASE_URL}/facility/username`);
    const params = { organizationName };
    url.search = new URLSearchParams(params).toString();

    const requestOptions = {
      method: 'GET',
      headers: reqHeaders,
      redirect: 'follow'
    };

    fetch(url, requestOptions)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        console.log(data);
        checkUserNameValidity(data.userName.length >= 2 && data.userName <= 20);
        setUserName(data.userName);
      })
      .catch(error => console.log('error', error));
  };

  function searchFacilitiesByNpi() {
    // Headers
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);
    reqHeaders.append('Content-Type', 'application/x-www-form-urlencoded');
    // Request Options
    const requestOptions = { method: 'GET', headers: reqHeaders, redirect: 'follow' };
    const url = new URL(`${BASE_URL}/facility/searchAllFacilitiesByNpi`);
    const params = { searchTerm: npi };
    url.search = new URLSearchParams(params).toString();
    fetch(url, requestOptions)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        setFacilityNpiSearchResults(data.facilities);
      })
      .catch(error => console.log('error', error));
  };

  function searchPracticeOwners() {
    const url = new URL(`${BASE_URL}/users/searchMedPros`);
    const params = { searchTerm: facilityUser };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        setFacilityUsersSearchResults(data.users ? data.users : []);
      })
      .catch(error => console.log('error', error));
  };
  // #endregion

  // #region Grid Toolbar handler functions

  const handleOnClickToggleUserDrawer = () => {
    toggleAddFacilityDrawer(true);
  };
  // #endregion

  // #region Data Grid Handler Functions
  const handleOnClickCell = (col, value) => {
    const idx = gridData.findIndex(el => el.facilityName === value);
    const facilityNpi = gridData[idx].npi;
    localStorage.setItem('lastBookmark', '/admin/facilities');
    history.push(`/facility/${facilityNpi}/profile`);
  };

  const handleOnClickResetFilters = () => {
    console.log('Grid reset function not implemented yet');
  };

  const handleOnPopupMenu = (e, idx) => {
    // console.log(e.pageX, e.pageY, idx);
    toggleGridEllipsisMenu(true);
    setCursorX(e.pageX);
    setCursorY(e.pageY);
    setPopupMenuIndex(idx);
  };

  const handleOnClickShowDetailsMenuItem = () => {
    const facilityNpi = gridData[popupMenuIndex].npi;
    localStorage.setItem('lastBookmark', '/admin/facilities');
    history.push(`/facility/${facilityNpi}/profile`);
  };

  const handleOnClickDeleteMenuItem = () => {
    const id = (gridData[popupMenuIndex]).id;
    toggleGridEllipsisMenu(false);
    deletePracticeProfile(id);
  };
  // #endregion

  // #region Edit Profile Form (Edit Handlers) & Save Profile

  const handleOnEditNpi = (e) => {
    const { value } = e.target;
    setNpi(value);
  };

  const handleOnEditFacilityName = (e) => {
    const { value } = e.target;
    setFacilityName(value);
  };

  const handleOnEditDisplayName = (e) => {
    const { value } = e.target;
    setDisplayNamePristineState(false);
    checkDisplayNameValidity(value.length >= 2 && value.length <= 40);
    setDisplayName(value);
  };

  const handleOnEditUserName = (e) => {
    const { value } = e.target;
    setUserNamePristineState(false);
    checkUserNameValidity(value.length >= 2 && value.length <= 20 && value.indexOf(' ') < 0);
    setUserName(value);
  };

  const handleOnEditStreetAddress = (e) => {
    const { value } = e.target;
    setStreetAddress(value);
  };

  const handleOnEditSuite = (e) => {
    const { value } = e.target;
    setSuite(value);
  };

  const handleOnEditCity = (e) => {
    const { value } = e.target;
    setCity(value);
  };

  const handleOnEditState = (e) => {
    const { value } = e.target;
    setState(value);
  };

  const handleOnEditZip = (e) => {
    const { value } = e.target;
    setZip(value);
  };

  const handleOnEditEmail = (e) => {
    const { value } = e.target;
    setEmailPristineState(false);
    checkEmailValidity(isEmail(value));
    setEmail(value);
  };

  const handleOnEditPhone = (e) => {
    const { value } = e.target;
    setPhonePristineState(false);
    checkPhoneValidity(isMobilePhone(value, 'en-US'));
    setPhone(value);
  };

  const handleOnEditFax = (e) => {
    const { value } = e.target;
    setFax(value);
  };

  const handleOnEditWebsite = (e) => {
    const { value } = e.target;
    setWebsite(value);
  };

  const handleOnChangeProfileDescription = (e) => {
    const { value } = e.target;
    setProfileDescription(value);
  };

  // Select search items from the last name search menu
  const selectFacility = (data) => {
    if (data.userName) {
      console.log('Facility already exists');
      return;
    }

    const { number, addresses, taxonomies } = data;
    const { organization_name: organizationName } = data.basic;

    const formattedOrgName = generateDisplayName(organizationName);
    setDisplayName(formattedOrgName);
    getUniqueFacilityUsername(formattedOrgName);
    setFacilityName(formattedOrgName);
    setIsFacilitySelected(true);
    setNpi(number);

    const taxIdx = taxonomies.findIndex(el => el.primary);
    if (taxIdx > -1) {
      setFacilityTaxonomy(taxonomies[taxIdx].desc);
    }

    if (Array.isArray(addresses) && addresses.length > 0) {
      const tmpIndex = addresses.findIndex(el => el.address_purpose === 'MAILING');
      if (tmpIndex > -1) {
        setStreetAddress(addresses[tmpIndex].address_1 || '');
        setCity(addresses[tmpIndex].city || '');
        setState(addresses[tmpIndex].state || '');
        setZip(addresses[tmpIndex].postal_code || '');
        setPhone(addresses[tmpIndex].telephone_number || '');
      }
    }
  };

  const addFacilityProfile = () => {
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('npi', npi);
    formData.append('facilityName', facilityName);
    formData.append('displayName', displayName);
    formData.append('userName', userName);
    formData.append('streetAddress', streetAddress);
    formData.append('suite', suite);
    formData.append('city', city);
    formData.append('state', state);
    formData.append('zip', zip);
    formData.append('email', email);
    formData.append('phone', phone);
    formData.append('fax', fax);
    formData.append('website', website);
    formData.append('avatarImageUrl', avatarImageUrl);
    formData.append('bannerImageUrl', bannerImageUrl);
    formData.append('profileDescription', profileDescription);
    // formData.append('facilityUsers', JSON.stringify(facilityUsers));
    formData.append('taxonomy', facilityTaxonomy);

    const requestOptions = {
      method: 'PUT',
      headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/facility/create`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          closeAddFacilityDrawer();
          getData();
        }
      })
      .catch(error => console.log('error', error));
  };

  const deletePracticeProfile = (_id) => {
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('_id', _id);

    const requestOptions = {
      method: 'DELETE',
      headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/practice/deletePractice`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          getData();
        }
      })
      .catch(error => console.log('error', error));
  };
  // #endregion

  // #region 'Practice Owners' tab eventhandlers
  const handleOnEditFacilityUser = (e) => {
    const { value } = e.target;
    setFacilityUser(value);
  };

  const closeFacilityUsesSearchMenu = () => {
    toggleFacilityUsersSearchMenu(false);
  };

  const handleOnClickAddFacilityUser = () => {
    if (!canAddFacilityUser) {
      return;
    }
    const updateFacilityUsers = facilityUsers.concat();
    updateFacilityUsers.push({
      fullName: facilityUser,
      npi: facilityUserNpi,
      id: facilityUserId,
      accessType: 'Facility User',
      avatarImageUrl: facilityUserAvatarImageUrl
    });
    setFacilityUsers(updateFacilityUsers);
    setFacilityUser('');
    setFacilityUsersSearchResults([]);
    toggleFacilityUsersSearchMenu(false);
    setCanAddFacilityUser(false);
  };

  const updateAccessType = (accessType, id) => {
    const updatedFacilityUsers = facilityUsers.concat();
    updatedFacilityUsers.forEach((el) => {
      if (el.id === id) {
        el.accessType = accessType;
      }
    });

    setFacilityUsers(updatedFacilityUsers);
  };

  const removeFacilityUser = (id) => {
    const updatedFacilityUsers = facilityUsers.filter(el => el.id !== id);
    setFacilityUsers(updatedFacilityUsers);
  };
  // #endregion

  // #region Banner image crop and file upload
  const uploadBanner = () => {
    const formData = new FormData();
    const img = bannerFileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setBannerImageUrl(res.fileUrl);
          setIsBannerUploading(false);
          setShowBannerCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadCroppedBanner = (blob) => {
    const formData = new FormData();
    formData.append('file', blob, 'croppedBanner.png');
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          // console.log('Cropped banner image url', res.fileUrl);
          setBannerImageUrl(res.fileUrl);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnBannerFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsBannerUploading(true);
    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setBannerImageUrl(res.fileUrl);
          setIsBannerUploading(false);
          setShowBannerCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnBannerFileSelected = () => {
    if (bannerFileInputRef.current.files.length > 0) {
      // console.log('Selected file = ', bannerFileInputRef.current.files[0]);
      // console.log('typeof img', typeof bannerFileInputRef.current.files[0]);
      setIsBannerUploading(true);
      uploadBanner();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickBannerCrop = () => {
    const imageElement = bannerCropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      drawerBannerRef.current.src = url;
      uploadCroppedBanner(blob);
      toggleBannerCropperDrawer(false);
    });
  };
  // #endregion

  // #region Avatar image crop and file upload

  const uploadAvatar = () => {
    const formData = new FormData();
    const img = avatarFileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setAvatarImageUrl(res.fileUrl);
          setIsAvatarUploading(false);
          setShowAvatarCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadCroppedAvatar = (blob) => {
    const formData = new FormData();
    formData.append('file', blob);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          // console.log('Cropped avatar image url', res.fileUrl);
          setAvatarImageUrl(res.fileUrl);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnAvatarFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsAvatarUploading(true);
    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          setAvatarImageUrl(res.fileUrl);
          setIsAvatarUploading(false);
          setShowAvatarCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnAvatarFileSelected = () => {
    if (avatarFileInputRef.current.files.length > 0) {
      // console.log('Selected file = ', avatarFileInputRef.current.files[0]);
      // console.log('typeof img', typeof avatarFileInputRef.current.files[0]);
      setIsAvatarUploading(true);
      uploadAvatar();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickAvatarCrop = () => {
    const imageElement = avatarCropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      drawerAvatarRef.current.src = url;
      uploadCroppedAvatar(blob);
      toggleAvatarCropperDrawer(false);
    });
  };
  // #endregion

  // Reset state when Add practice drawer is closed
  const resetState = (closeDrawer = true) => {
    toggleBannerCropperDrawer(false);
    toggleAvatarCropperDrawer(false);
    if (closeDrawer) {
      toggleAddFacilityDrawer(false);
    }
    setDisplayName('');
    checkDisplayNameValidity(false);
    setDisplayNamePristineState(true);
    setUserName('');
    checkUserNameValidity(false);
    setUserNamePristineState(true);
    setUserNameDuplicateStatus(true);
    setStreetAddress('');
    setSuite('');
    setCity('');
    setState('');
    setZip('');
    setPhone('');
    checkPhoneValidity(false);
    setPhonePristineState(true);
    setFax('');
    setProfileDescription('');
    setBannerImageUrl('https://quantumtrader.s3.amazonaws.com/defaultBanner.svg');
    setAvatarImageUrl('https://quantumtrader.s3.amazonaws.com/defaultAvatar.svg');
    setShowBannerCropper(false);
    setIsBannerUploading(false);
    setShowAvatarCropper(false);
    setIsAvatarUploading(false);
    setEmail('');
    checkEmailValidity(false);
    setEmailPristineState(true);
    setEmailDuplicateStatus(true);
    setNpi('');
    setWebsite('');
    setCanAddFacility(false);
    setFacilityNpiSearchResults([]);
    setActiveTab(0);
    setFacilityName('');
    setFacilityUser('');
    setFacilityUserNpi('');
    setFacilityUserId('');
    setFacilityUserAvatarImageUrl('');
    setCanAddFacilityUser(false);
    toggleFacilityUsersSearchMenu(false);
    setFacilityUsersSearchResults([]);
    setFacilityUsers([]);
    setIsFacilitySelected(false);
  };

  const closeAddFacilityDrawer = () => {
    toggleAddFacilityDrawer(false);
    resetState();
  };

  // Save practice profile
  const handleOnClickSaveFacilityProfile = () => {
    if (!canAddFacility) {
      return;
    }
    addFacilityProfile();
  };

  // #region Various Menu Items

  // User search results
  // const items = [];

  // Last name search results (Change it to NPI)
  const facilitySearchItems = [];
  if (facilityNpiSearchResults && facilityNpiSearchResults.length > 0) {
    facilityNpiSearchResults.forEach((el) => {
      const { taxonomies } = el;
      let facTaxonomy = {};
      if (Array.isArray(taxonomies) && taxonomies.length > 0) {
        for (let i = 0; i < taxonomies.length; i++) {
          if (taxonomies[i].primary) {
            facTaxonomy = taxonomies[i];
            el.facTaxonomy = taxonomies[i].desc;
            break;
          }
        }
      }
      facilitySearchItems.push(
        <div
          key={el.number}
          className={`fx p-2 ${css(styles.userSearchItems)}`}
          onClick={() => { selectFacility(el); }}
        >
          {/* User Avatar */}
          <div
            className={css(styles.userSearchItemsIcon)}
            style={{ width: '52px', height: '52px' }}
          >
            {el.avatarImageUrl
              ? <img src={el.avatarImageUrl} style={{ height: '52px', width: '52px' }} />
              : <i className='fa fa-building-o' />
            }
          </div>
          {/* Name, NPI and username (if available) */}
          <div
            className='ml-2'
            style={{ fontSize: '12px', marginTop: '-4px', lineHeight: '14px' }}
          >
            <div className='fx' style={{ flexFlow: 'column' }}>
              <div style={{ fontWeight: '700' }}>
                <span>{titleCase(el.basic.organization_name)}</span>
              </div>
              <div>
                {facTaxonomy.desc}
              </div>
              <div style={{ color: blue[500] }}>
                {el.userName ? `@${el.userName}` : ''}
              </div>
            </div>
            <div>
              {el.number}
            </div>
          </div>
        </div>
      );
    });
  }

  // Facility Users search results
  const facUsers = [];
  if (facilityUsersSearchResults.length > 0) {
    facilityUsersSearchResults.forEach(el => {
      facUsers.push(
        <div
          className='m-item'
          style={{
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid #001824',
            cursor: 'pointer',
            fontSize: '12px',
            background: gray[700]
          }}
          onClick={() => {
            setFacilityUser(`${el.lastName}, ${el.firstName}`);
            setFacilityUserNpi(el.npi ? el.npi : 'NPI n/a');
            setFacilityUserId(el._id);
            setFacilityUserAvatarImageUrl(el.avatarImageUrl);
            toggleFacilityUsersSearchMenu(false);
            setCanAddFacilityUser(true);
          }}
        >
          <div>
            {`${el.lastName}, ${el.firstName} (${el.npi ? el.npi : 'NPI n/a'})`}
          </div>
        </div>
      );
    });
  }

  // Added practice owners cards
  const addedFacilityUsers = [];
  if (facilityUsers.length > 0) {
    facilityUsers.forEach(el => {
      console.log(el);
      addedFacilityUsers.push(
        <PracticeUserCard
          el={el}
          removePracticeOwner={removeFacilityUser}
          updateAccessType={updateAccessType}
        />
      );
    });
  }

  // #endregion

  // #region Facility Search Event Handlers
  const resetFacilitySearchDrawerState = () => {
    setSearchOrgName('');
    setSearchTaxonomy('');
    setSearchNpi('');
    setSearchCity('');
    setSearchZip('');
    setShowStateMenu(false);
    setSearchState('');
    setSearchingFacilities(false);
    setFacilitySearchResults([]);
    setShowZeroResultMessage(false);
  };

  const handleOnChangeSearchOrgName = (e) => {
    const { value } = e.target;
    setShowZeroResultMessage(false);
    setSearchOrgName(value);
  };

  const handleOnChangeSearchTaxonomy = (e) => {
    const { value } = e.target;
    setShowZeroResultMessage(false);
    setSearchTaxonomy(value);
  };

  const handleOnChangeSearchNpi = (e) => {
    const { value } = e.target;
    setShowZeroResultMessage(false);
    setSearchNpi(value);
  };

  const handleOnChangeSearchCity = (e) => {
    const { value } = e.target;
    setShowZeroResultMessage(false);
    setSearchCity(value);
  };

  const handleOnChangeSearchZip = (e) => {
    const { value } = e.target;
    setShowZeroResultMessage(false);
    setSearchZip(value);
  };

  const handleOnClickResetFacilitySearch = () => {
    setSearchOrgName('');
    setSearchTaxonomy('');
    setSearchNpi('');
    setSearchCity('');
    setSearchState('');
    setSearchZip('');
    setFacilitySearchResults([]);
    setFacilityNpiSearchResults([]);
  };

  const handleOnClickSearchFacilities = () => {
    if (searchingFacilities) {
      controller.abort();
      resetFacilitySearchDrawerState();
      return;
    }

    controller = new AbortController();
    // const url = new URL(`${BASE_URL}/users/searchFacilityNppes`);
    const url = new URL(`${BASE_URL}/facility/searchFacilityNppes`);
    const params = {
      orgName: searchOrgName,
      taxonomy: searchTaxonomy,
      npi: searchNpi,
      state: searchState === 'ALL' ? '' : searchState,
      city: searchCity,
      zip: searchZip
    };
    url.search = new URLSearchParams(params).toString();

    setSearchingFacilities(true);
    fetch(url, { signal: controller.signal })
      .then(response => response.text())
      .then(result => {
        const data = JSON.parse(result);
        setSearchingFacilities(false);
        if (data.facilities.some(p => p.enumeration_type === 'NPI-1')) {
          props.addAppAlert({
            headerText: 'Warning',
            headerIcon: 'fa fa-bell',
            bodyText: 'Only type 2 NPI search allowed!',
            showAlert: true,
            autoDismiss: true,
            autoDismissTime: 5000
          });
          setFacilitySearchResults([]);
          setSearchOrgName('');
          setSearchTaxonomy('');
          setSearchNpi('');
          setSearchNpi('');
          setSearchCity('');
          setSearchState('');
          setSearchZip('');
        } else {
          data.facilities.sort((a, b) => b.hasMedprosProfile - a.hasMedprosProfile);
          setFacilitySearchResults(data.facilities);
          if (data.facilities.length === 0) {
            setShowZeroResultMessage(true);
          }
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnClickToggleFacilitySearchDrawer = () => {
    resetFacilitySearchDrawerState();
    setShowSearchFacilityDrawer(true);
  };

  const handleOnCloseSearchFacilityDrawer = () => {
    setShowSearchFacilityDrawer(false);
  };
  // #endregion

  return (
    <div
      className={css(styles.gridParent)}
      ref={gridParentRef}
    >
      {/* Search Facility Drawer */}
      {showSearchFacilityDrawer &&
        <Drawer
          placement="right"
          visible={showSearchFacilityDrawer}
          toggleDrawer={handleOnCloseSearchFacilityDrawer}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 358}
        >
          {/* Drawer Container */}
          <div className={css(styles.drawerContainer)}>
            {/* Header */}
            <div className={css(styles.drawerHeader)}>
              <div className='fx fx__hv-center'>
                <i className='mr-2 fa fa-lg fa-building' />
                <div className='noselect'>Search Facility</div>
              </div>
              <i
                className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
                onClick={handleOnCloseSearchFacilityDrawer}
              />
            </div>
            {/* Middle Area */}
            <div className={css(styles.drawerContentArea)}>
              <div className='pl-2 pr-2'>
                <div className='mt-3'>
                  {/* Organization Name */}
                  <div>
                    <TextInputWithLabel
                      value={searchOrgName}
                      titleText="Org Name"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={334}
                      placeholder={!showNpiErrMessage && searchNpi.length === 10 ? null : 'Required'}
                      onChange={handleOnChangeSearchOrgName}
                    />
                  </div>
                  {/* Taxonomy and NPI */}
                  <div className='fx'>
                    {/* Taxonomy */}
                    <TextInputWithLabel
                      value={searchTaxonomy}
                      titleText="Taxonomy"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={163}
                      onChange={handleOnChangeSearchTaxonomy}
                    />
                    {/* NPI */}
                    <TextInputWithLabel
                      value={searchNpi}
                      className='ml-2'
                      titleText="NPI"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={163}
                      onChange={handleOnChangeSearchNpi}
                    />
                  </div>
                  {showNpiErrMessage &&
                    <div
                      className={css(styles.npiErrMessageContainer)}
                    >
                      <div className={css(styles.npiErrMessage)}>
                        {npiErrMsg}
                      </div>
                    </div>
                  }
                  {/* City, State & Zip */}
                  <div className='fx' style={{ alignItems: 'flex-end' }}>
                    <TextInputWithLabel
                      value={searchCity}
                      titleText="City"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={192}
                      onChange={handleOnChangeSearchCity}
                    />
                    <div
                      ref={stateRef}
                      style={{ position: 'relative' }}
                    >
                      <div
                        className='ml-2'
                        onClick={() => setShowStateMenu(true)}
                        style={{
                          height: '24px',
                          width: '43px',
                          background: '#103A60',
                          color: blue[200],
                          fontSize: '12px',
                          padding: '2px',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          bottom: '-2px'
                        }}
                      >
                        {`${searchState.length === 0 ? 'ALL' : searchState} `}
                        <i
                          style={{ fontSize: '16px' }}
                          className={`ml-1 fa ${showStateMenu ? 'fa-caret-up' : 'fa-caret-down'}`}
                        />
                      </div>
                      <PopupMenu
                        top={28}
                        left={11}
                        zIndex='1550'
                        position="absolute"
                        visible={showStateMenu}
                        parent={stateRef}
                        onOutsideClick={() => setShowStateMenu(false)}
                      >
                        <div
                          style={{
                            background: '#0a3250',
                            width: '128px',
                            height: '200px',
                            overflowY: 'auto'
                          }}
                        >
                          {USA_STATES.map(el => (
                            <div
                              key={el.code}
                              className='m-item'
                              onClick={() => {
                                setShowZeroResultMessage(false);
                                setSearchState(el.code);
                                setShowStateMenu(false);
                              }}
                              style={{
                                height: '32px',
                                borderBottom: '1px solid #001824',
                                cursor: 'pointer',
                                fontSize: '12px',
                                background: gray[700]
                              }}
                            >
                              {el.name}
                            </div>
                          ))}
                        </div>
                      </PopupMenu>
                    </div>
                    <TextInputWithLabel
                      value={searchZip}
                      className='ml-2'
                      titleText="Zip"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={80}
                      onChange={handleOnChangeSearchZip}
                    />
                  </div>
                  {/* Search Button */}
                  <div className='mt-4 fx fx__align-right'>
                    <Button
                      btnType='primary'
                      isOutlineBtn
                      isDisabled={!canSearch}
                      onClick={handleOnClickResetFacilitySearch}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        width: '100px',
                        height: '32px'
                      }}
                    >
                      Reset
                    </Button>
                    <Button
                      className='ml-2'
                      isDisabled={!canSearch}
                      showLoader={searchingFacilities}
                      btnType='primary'
                      onClick={handleOnClickSearchFacilities}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '240px',
                        height: '32px'
                      }}
                    >
                      {searchingFacilities ? 'Searching... Click to Cancel' : 'Search'}
                    </Button>
                  </div>
                  {showZeroResultMessage &&
                    <div className='p-2' style={{ color: salmon[500], fontSize: '14px', fontWeight: '700' }}>
                      No Facilities Found
                    </div>
                  }
                </div>
                {/* Facilities Cards */}
                <div
                  className='mt-2 pl-2 pr-2'
                  style={{
                    height: 'calc(100vh - 250px)',
                    overflowY: 'auto'
                  }}
                >
                  {facilitySearchResults.map(el => {
                    const { organization_name: orgName } = el.basic;
                    const taxIdx = el.taxonomies.findIndex(el => el.primary);
                    const addIdx = el.addresses.findIndex(el => el.address_purpose === 'MAILING');

                    return (
                      <div
                        key={`${el.number}__provider`}
                        className='fx mt-2 p-1'
                        style={{
                          background: blue[700],
                          borderRadius: '4px',
                          borderTop: `1px solid ${blue[600]}`,
                          fontSize: '14px',
                          fontWeight: '700',
                          minHeight: '49px'
                        }}
                      >
                        {/* Data */}
                        <div className='mr-2' style={{ flexGrow: '1' }}>
                          <div style={{ color: blue[400] }}>
                            <div className='line-clamp_2'>
                              {orgName.length > 0 ? `${titleCase(orgName)}` : ''}
                            </div>
                            <a
                              target='_blank'
                              rel="noreferrer"
                              href={`https://npiregistry.cms.hhs.gov/provider-view/${el.number}`}
                            >
                              {el.number}
                              <i
                                className='ml-1 font-8 fa fa-external-link'
                                style={{ marginTop: '-8px', position: 'relative', top: '-4px' }}
                              />
                            </a>
                          </div>
                          <div className='mt-1'>
                            <div>{el.taxonomies[taxIdx].desc}</div>
                            <div>
                              {titleCase(el.addresses[addIdx].city)}
                              {el.addresses[addIdx].state.length > 0 ? `, ${el.addresses[addIdx].state.toUpperCase()}` : ''}
                            </div>
                          </div>
                        </div>
                        {/* Icons/buttons */}
                        <div className='pr-1'>
                          <Button
                            isDisabled={el.hasMedprosProfile}
                            style={{
                              maxWidth: '130px',
                              minWidth: '130px',
                              maxHeight: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginTop: '4px',
                              fontSize: '14px'
                            }}
                            onClick={() => {
                              if (!el.hasMedprosProfile) {
                                setShowSearchFacilityDrawer(false);
                                toggleAddFacilityDrawer(true);
                                selectFacility(el);
                              }
                            }}
                          >
                            {el.hasMedprosProfile ? 'Duplicate' : 'Create Profile'}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </Drawer>
      }

      {/* Add facility drawer */}
      {isAddFacilityDrawerVisible &&
        <Drawer
          placement="right"
          visible={isAddFacilityDrawerVisible}
          toggleDrawer={closeAddFacilityDrawer}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 358}
        >
          {/* Drawer Container */}
          <div className={css(styles.drawerContainer)}>
            {/* Header */}
            <div className={css(styles.drawerHeader)}>
              <div className='fx fx__hv-center'>
                <i className='mr-2 fa fa-lg fa-building' />
                <div className='noselect'>Create Facility Profile</div>
              </div>
              <i
                className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
                onClick={closeAddFacilityDrawer}
              />
            </div>
            {/* Active Tab */}
            <div className={css(styles.drawerContentArea)}>
              <div className='p-2'>
                {/* Banner */}
                <div className={css(styles.drawerBanner)}>
                  <img
                    ref={drawerBannerRef}
                    src={bannerImageUrl}
                    alt="cropped-banner"
                    className={css(styles.drawerBannerImg)}
                  />
                  {/* Camera icon */}
                  <div
                    onClick={() => { toggleBannerCropperDrawer(true); }}
                    className={css(styles.drawerBannerCameraIcon)}
                  >
                    <i className="fa fa-camera" />
                  </div>
                </div>

                {/* Middle area */}
                <div>
                  {/* NPI  */}
                  <div
                    style={{
                      position: 'relative',
                      marginTop: '-5px'
                    }}
                    ref={lastNameSearchMenu}
                  >
                    <div className='fx' style={{ position: 'relative' }}>
                      NPI: {npi}
                    </div>
                  </div>
                  {/* Practice Name */}
                  <div>
                    <TextInputWithLabel
                      value={facilityName}
                      titleText='Facility Name*'
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={342}
                      onChange={handleOnEditFacilityName}
                    />
                  </div>
                  {/* User card */}
                  <div className="fx mt-4 ml-2">
                    {/* Avatar */}
                    <div className={css(styles.drawerUserCardAvatar)}>
                      <img
                        src={avatarImageUrl}
                        ref={drawerAvatarRef}
                        alt="avatar"
                        className={css(styles.drawerUserCardAvatarImg)}
                        onClick={() => toggleAvatarCropperDrawer(true)}
                      />
                      <div
                        onClick={() => { toggleAvatarCropperDrawer(true); }}
                        className={css(styles.drawerAvatarCamera)}
                      >
                        <i className="fa fa-camera" />
                      </div>
                    </div>
                    {/* User card displayName & userName */}
                    <div className={`ml-2 ${css(styles.drawerUserCardText)}`}>
                      {/* Display Name */}
                      <div style={{ marginTop: '-5px' }}>
                        <TextInputWithLabel
                          value={displayName}
                          titleText="Display Name (2 to 40 characters)*"
                          titleFontSize={12}
                          isCompactLayout={true}
                          titleColor={gray[200]}
                          textColor="white"
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={248}
                          onChange={handleOnEditDisplayName}
                        />
                        {/* Error message */}
                        {!isDisplayNameValid && !isDisplayNamePristine
                          ? <div className={css(styles.errMsg)}>
                            Invalid Display Name
                          </div>
                          : null
                        }
                      </div>
                      {/* UserName */}
                      <div
                        style={{
                          marginTop: (!isDisplayNameValid && !isDisplayNamePristine) ? '0px' : '4px'
                        }}
                      >
                        <TextInputWithLabel
                          value={userName}
                          titleText="Username (2 to 20 characters, no spaces)*"
                          titleFontSize={12}
                          isCompactLayout={true}
                          titleColor={gray[200]}
                          textColor={blue[500]}
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={248}
                          onChange={handleOnEditUserName}
                        />
                        {/* Error message */}
                        {!isUserNameValid && !isUserNamePristine
                          ? <div className={css(styles.errMsg)}>
                            Invalid User Name
                          </div>
                          : null
                        }
                        {isUserNameValid && !isUserNamePristine && isUserNameDuplicate
                          ? <div className={css(styles.errMsg)}>
                            User Name Taken
                          </div>
                          : null
                        }
                      </div>
                    </div>
                  </div>
                  {/* Mailing Address, Email, Phone, Fax, Profile description & Website */}
                  <div className="mt-4 ml-2">
                    {/* Heading */}
                    <div className="fx fx__v-center">
                      <i className="fa fa-map-marker" style={{ fontSize: '22px' }} />
                      <div className="ml-1">
                        Mailing Address
                      </div>
                    </div>
                    {/* Form */}
                    <div>
                      {/* Address */}
                      <div className="fx">
                        <div>
                          <TextInputWithLabel
                            value={streetAddress}
                            titleText="Street"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={284}
                            onChange={handleOnEditStreetAddress}
                          />
                        </div>
                        <div className="ml-2">
                          <TextInputWithLabel
                            value={suite}
                            titleText="Suite"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={45}
                            onChange={handleOnEditSuite}
                          />
                        </div>
                      </div>
                      {/* City, State and Zip */}
                      <div className="fx mt-2">
                        <div>
                          <TextInputWithLabel
                            value={city}
                            titleText="City"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={230}
                            onChange={handleOnEditCity}
                          />
                        </div>
                        <div className="ml-2">
                          <TextInputWithLabel
                            value={state}
                            titleText="State"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={45}
                            onChange={handleOnEditState}
                          />
                        </div>
                        <div className="ml-2">
                          <TextInputWithLabel
                            value={zip}
                            titleText="Zip"
                            titleFontSize={12}
                            isCompactLayout={true}
                            titleColor={gray[200]}
                            textColor="white"
                            selectionColor="white"
                            selectionBackground="#0080FF"
                            spellcheck={false}
                            width={45}
                            onChange={handleOnEditZip}
                          />
                        </div>
                      </div>
                      {/* Email */}
                      <div className="mt-2">
                        <TextInputWithLabel
                          value={email}
                          titleText="Email*"
                          titleFontSize={12}
                          isCompactLayout={false}
                          titleColor={gray[200]}
                          textColor="white"
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={337}
                          showPreIcon
                          preIcon='fa fa-envelope'
                          onChange={handleOnEditEmail}
                        />
                        {!isEmailPristine && !isEmailValid
                          ? <div className={css(styles.errMsg)}>Invalid Email</div>
                          : null
                        }
                        {isEmailValid && !isEmailPristine && isEmailDuplicate
                          ? <div className={css(styles.errMsg)}>Email already in use</div>
                          : null
                        }
                      </div>
                      {/* Phone */}
                      <div className="mt-2">
                        <TextInputWithLabel
                          value={phone}
                          titleText="Phone #*"
                          titleFontSize={12}
                          isCompactLayout={false}
                          titleColor={gray[200]}
                          textColor="white"
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={337}
                          showPreIcon
                          preIcon='fa fa-phone'
                          onChange={handleOnEditPhone}
                        />
                        {!isPhonePristine && !isPhoneValid
                          ? <div className={css(styles.errMsg)}>Invalid Phone #</div>
                          : null
                        }
                      </div>
                      {/* Fax */}
                      <div className="mt-2">
                        <TextInputWithLabel
                          value={fax}
                          titleText="Fax #"
                          titleFontSize={12}
                          isCompactLayout={false}
                          titleColor={gray[200]}
                          textColor="white"
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={337}
                          showPreIcon
                          preIcon='fa fa-fax'
                          onChange={handleOnEditFax}
                        />
                      </div>
                      {/* Website */}
                      <div className="mt-2">
                        <TextInputWithLabel
                          value={website}
                          titleText="Website"
                          titleFontSize={12}
                          isCompactLayout={false}
                          titleColor={gray[200]}
                          textColor="white"
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={337}
                          showPreIcon
                          preIcon='fa fa-globe'
                          onChange={handleOnEditWebsite}
                        />
                      </div>
                    </div>
                  </div>
                  {/* Profile descriptiom */}
                  <div className="mt-4 ml-2">
                    <div>Profile Description</div>
                    <div className={css(styles.profileTextAreaContainer)}>
                      <textarea
                        className={css(styles.profileTextArea)}
                        placeholder="Write a description"
                        value={profileDescription}
                        onChange={handleOnChangeProfileDescription}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Footer */}
            <div className={`fx pr-2 ${css(styles.drawerFooter)}`} >
              {/* Required fields msg */}
              <div className={css(styles.criticalSmallText)} >
                *Required fields
              </div>
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                onClick={handleOnClickSaveFacilityProfile}
                isDisabled={!canAddFacility}
                style={{
                  height: '32px',
                  width: '340px',
                  padding: '3px 0'
                }}
              >
                Create Facility Profile
              </Button>
            </div>
          </div>
        </Drawer>
      }

      {/* -- Profile banner image cropper ------------------------------- */}
      <Drawer
        placement="right"
        visible={isBannerCropperDrawerOpen}
        toggleDrawer={() => toggleBannerCropperDrawer(false)}
        zIndex="2050"
        width={window.innerWidth <= 700 ? window.innerWidth : 358}
      >
        <div className={css(styles.drawerContainer)}>
          {/* Header */}
          <div className={css(styles.drawerHeader)}>
            <div>Upload Profile Banner</div>
            <i
              className={`fa fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
              onClick={() => { toggleBannerCropperDrawer(false); }}
            />
          </div>
          {/* Content Area */}
          <div className={css(styles.drawerContentArea, styles.imageUploaderContainer)}>
            {showBannerCropper
              ? <div>
                <Cropper
                  src={bannerImageUrl}
                  style={{ height: 400, width: '100%' }}
                  checkCrossOrigin
                  checkOrientation={false}
                  aspectRatio={6}
                  background={false}
                  viewMode={1}
                  autoCropArea={1}
                  guides={false}
                  ref={bannerCropperRef}
                />
              </div>
              : null
            }
            {!showBannerCropper
              ? <div>
                <FileUploader
                  parentRef={bannerFileInputRef}
                  onDrop={handleOnBannerFileDrop}
                  onChange={handleOnBannerFileSelected}
                />
              </div>
              : null
            }
            {isBannerUploading
              ? <div className={css(styles.imageUploadLoader)}>
                <Loader iconType="cog" iconSize="5x" text="Uploading Banner Image" />
              </div>
              : null
            }
          </div>
          {/* Drawer footer area */}
          <div
            className={`fx pr-2 ${css(styles.drawerFooter)}`}
            style={{ position: 'absolute', bottom: '0' }}
          >
            <Button
              btnType="primary"
              className="fx fx__hv-center"
              onClick={handleOnClickBannerCrop}
              style={{
                height: '32px',
                width: '340px',
                padding: '3px 0'
              }}
            >
              Crop Banner Image
            </Button>
          </div>
        </div>
      </Drawer>

      {/* -- Profile avatar image cropper ------------------------------- */}
      <Drawer
        placement="right"
        visible={isAvatarCropperDrawerOpen}
        toggleDrawer={() => toggleAvatarCropperDrawer(false)}
        zIndex="2050"
        width={window.innerWidth <= 700 ? window.innerWidth : 358}
      >
        <div className={css(styles.drawerContainer)}>
          {/* Header */}
          <div className={css(styles.drawerHeader)}>
            <div>Upload Profile Avatar</div>
            <i
              className={`fa fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
              onClick={() => { toggleAvatarCropperDrawer(false); }}
            />
          </div>
          {/* Content Area */}
          <div className={css(styles.drawerContentArea, styles.imageUploaderContainer)}>
            {showAvatarCropper
              ? <div>
                <Cropper
                  src={avatarImageUrl}
                  style={{ height: 400, width: '100%' }}
                  checkCrossOrigin
                  checkOrientation={false}
                  aspectRatio={1}
                  background={false}
                  viewMode={1}
                  autoCropArea={1}
                  guides={false}
                  ref={avatarCropperRef}
                />
              </div>
              : null
            }
            {!showAvatarCropper
              ? <div>
                <FileUploader
                  parentRef={avatarFileInputRef}
                  onDrop={handleOnAvatarFileDrop}
                  onChange={handleOnAvatarFileSelected}
                />
              </div>
              : null
            }
            {isAvatarUploading
              ? <div className={css(styles.imageUploadLoader)}>
                <Loader iconType="cog" iconSize="5x" text="Uploading Avatar Image" />
              </div>
              : null
            }
          </div>
          {/* Drawer footer area */}
          <div
            className={`fx pr-2 ${css(styles.drawerFooter)}`}
            style={{ position: 'absolute', bottom: '0' }}
          >
            <Button
              btnType="primary"
              className="fx fx__hv-center"
              onClick={handleOnClickAvatarCrop}
              style={{
                height: '32px',
                width: '340px',
                padding: '3px 0'
              }}
            >
              Crop Avatar Image
            </Button>
          </div>
        </div>
      </Drawer>

      {/* Grid toolbar and users data grid */}
      <div className={css(styles.gridContainer)}>
        {/* Grid toolbar */}
        <div className={css(styles.gridToolbar)}>
          {/* Grid toolbar - right aligned buttons */}
          <div className='fx'>
            {/* Add user button */}
            <div
              className={css(styles.gridToolbarIcon)}
              onClick={handleOnClickToggleFacilitySearchDrawer}
            >
              <i className="fa fa-lg fa-plus-circle" />
            </div>
            {/* Download */}
            <div className={css(styles.gridToolbarIcon)}>
              <i className="fa fa-lg fa-cloud-download" />
            </div>
            {/* Trash bin */}
            <div className={css(styles.gridToolbarIcon)}>
              <i className="fa fa-lg fa-trash" />
            </div>
            {/* Checkbox */}
            <div className={css(styles.gridToolbarIcon)}>
              <i className="fa fa-lg fa-check-square" />
            </div>
            {/* Reset button */}
            <div
              className={css(styles.gridToolbarIcon)}
              onClick={handleOnClickResetFilters}
            >
              <i className="fa fa-lg fa-refresh" />
            </div>
            {/* Vertical ellipsis */}
            <div
              className={css(styles.gridToolbarIcon)}
              style={{ fontSize: '16px' }}
            >
              <span className='fa-stack'>
                <i className='fa fa-circle fa-stack-2x' />
                <i
                  className='fa fa-ellipsis-v fa-stack-1x'
                  style={{ color: gray[700] }}
                />
                {/* <i class="fa fa-flag fa-stack-1x fa-inverse"></i> */}
              </span>
            </div>
          </div>
        </div>
        {/* Grid ellipsis menu */}
        <div
          ref={gridEllipsisMenu}
          className={css(styles.gridEllipsisMenuParent)}
          style={{
            top: cursorY,
            left: cursorX - 140,
            display: showGridEllipsisMenu ? 'block' : 'none'
          }}
        >
          <PopupMenu
            top={0}
            left={0}
            position="relative"
            visible={showGridEllipsisMenu}
            parent={gridEllipsisMenu}
            onOutsideClick={() => { toggleGridEllipsisMenu(false); }}
          >
            <div className={css(styles.gridEllipsisMenuContainer)}>
              {/* Show details menu item */}
              <div
                className={css(styles.gridEllipsisMenuItem)}
                onClick={handleOnClickShowDetailsMenuItem}
              >
                Show Details
              </div>
              {/* Delete record menu item */}
              <div
                className={css(styles.gridEllipsisMenuItem)}
                onClick={handleOnClickDeleteMenuItem}
              >
                Delete Record
              </div>
            </div>
          </PopupMenu>
        </div>
        {/* Users' Data Grid */}
        <DataGrid
          loader={showLoader}
          icons={icons}
          headerDefs={headerDefs}
          rowData={gridData}
          filters={filters}
          gridWidth={gridWidth}
          gridHeight={gridHeight}
          gridHeaderHeight={48}
          gridFooterHeight={0}
          gridContainerHeight='730px'
          rowHeight={64}
          rowsPerPage={DOCS_PER_PAGE}
          defaultCellStyle={defaultCellStyle}
          showFooter={false}
          showPager
          showEllipsisCol
          enableHover
          totalPages={Math.ceil(docsCount / DOCS_PER_PAGE)}
          currentPage={currentPage}
          isMobileLayout={isMobileLayout}
          onClickCell={handleOnClickCell}
          onApplyFilter={() => { console.warn('Handler not set for onApplyFilter prop.'); }}
          onResetCol={() => { console.warn('Handler not set for onResetCol prop.'); }}
          onLoadPage={() => { console.warn('Handler not set for onLoadPage prop.'); }}
          onColFilterToggle={() => { console.warn('Handler not set for onColFilterToggle prop.'); }}
          onFilterTypeUpdate={() => { console.warn('Handler not set for onFilterTypeUpdate prop.'); }}
          onColPinChange={() => { console.warn('Handler not set for onColPinChange prop.'); }}
          onResize={() => { console.warn('Handler not set for onResize prop.'); }}
          onPopupMenu={handleOnPopupMenu}
          onColVisibilityChange={() => { console.warn('Handler not set for onColVisibilityChange prop.'); }}
          onFilterChange={() => { console.warn('Handler not set for onFilterChange prop.'); }}
          onChangeTextAlignment={() => { console.warn('Handler not set for onChangeTextAlignment prop.'); }}
          onResetGrid={() => { console.warn('Handler not set for onResetGrid prop.'); }}
          onSort={() => { console.warn('Handler not set for onSort prop.'); }}
          onColConfig={() => { console.warn('Handler not set for onColConfig prop.'); }}
        />
      </div>
    </div>
  );
};

function mapStateToProps({ user }) {
  return user;
}
export default connect(
  mapStateToProps,
  { addAppAlert })(FacilityDataGrid);
